import { useEffect } from "react";

import {
  DOCS_LINK,
  DOCS_REPORT_CRITICAL_INCIDENT,
} from "src/constants/ExternalLinks";
import { useUserData } from "src/store/AuthStore";

export const WEBCHAT_SCRIPT_ID = "webchat-script";
export const WEBCHAT_SCRIPT_LOADED_EVENT = "webchat-script-loaded";
export const WEBCHAT_WINDOW_TOGGLE_EVENT = "webchat-window-toggle";
const TAKTILE_LOGO_URL =
  "https://25449300.fs1.hubspotusercontent-eu1.net/hubfs/25449300/logo.png";
const WEBCHAT_CONTAINER_ID = "webchat-container";
const WEBCHAT_SCRIPT_SRC = "https://chat.cdn-plain.com/index.js";

type WebchatConfig = {
  appId: string;
  hideLauncher: boolean;
  links: {
    icon: string;
    text: string;
    url: string;
  }[];
  chatButtons: {
    icon: string;
    text: string;
  }[];
  entryPoint: {
    type: "chat" | "default";
    externalId?: string;
    singleChatMode?: boolean;
  };
  embedAt?: HTMLElement | null;
  hideBranding: boolean;
  theme: "light" | "dark" | "auto";
  style: {
    brandColor: string;
    brandBackgroundColor: string;
    launcherBackgroundColor: string;
    launcherIconColor: string;
  };
  logo: {
    url: string;
    alt: string;
  };
  position?: {
    bottom: string;
    right: string;
  };
  customerDetails: {
    email: string;
    emailHash: string;
    externalId: string;
    fullName: string;
    shortName?: string;
    chatAvatarUrl?: string;
  };
};

type WebchatWindow = Window & {
  webchatWidget?: WebchatConfig;
  webchatKey?: string;
  isWidgetOpen?: boolean;
  Plain?: {
    init: (config: WebchatConfig) => Promise<void>;
    open: () => void;
    close: () => void;
    onOpen: (callback: VoidFunction) => void;
    onClose: (callback: VoidFunction) => void;
  };
};

const injectWebchatContainer = () => {
  if (document.getElementById(WEBCHAT_CONTAINER_ID)) {
    return;
  }

  const container = document.createElement("div");
  container.id = WEBCHAT_CONTAINER_ID;
  container.style.zIndex = "1000";
  container.style.position = "fixed";
  document.body.appendChild(container);
};

const injectWebchatScript = (config: WebchatConfig) => {
  if (!document || document.getElementById(WEBCHAT_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement("script");
  script.id = WEBCHAT_SCRIPT_ID;
  script.async = false;
  script.src = WEBCHAT_SCRIPT_SRC;
  script.onload = async () => {
    if ("Plain" in window) {
      const webchatWindow = window as WebchatWindow;
      await webchatWindow.Plain!.init(config);
      document.dispatchEvent(new CustomEvent(WEBCHAT_SCRIPT_LOADED_EVENT));

      webchatWindow.Plain!.onOpen(() => {
        webchatWindow.isWidgetOpen = true;
        document.dispatchEvent(
          new CustomEvent(WEBCHAT_WINDOW_TOGGLE_EVENT, {
            detail: { isOpen: true },
          }),
        );
      });
      webchatWindow.Plain!.onClose(() => {
        webchatWindow.isWidgetOpen = false;
        document.dispatchEvent(
          new CustomEvent(WEBCHAT_WINDOW_TOGGLE_EVENT, {
            detail: { isOpen: false },
          }),
        );
      });
    }
  };
  document.body.appendChild(script);
};

const buildWebchatConfig = ({
  email,
  name,
  hashedEmail,
  userId,
  avatarUrl,
}: {
  email: string;
  name: string;
  hashedEmail: string;
  userId: string;
  avatarUrl: string;
}): WebchatConfig => {
  const webchatWindow = window as WebchatWindow;
  const webchatKey = webchatWindow.webchatKey;
  if (!webchatKey) {
    throw new Error("Webchat key is not set");
  }

  return {
    appId: webchatKey,
    hideBranding: true,
    theme: "light",
    hideLauncher: true,
    embedAt: document.getElementById(WEBCHAT_CONTAINER_ID),
    logo: {
      url: TAKTILE_LOGO_URL,
      alt: "Taktile",
    },
    position: {
      bottom: "22px",
      right: "calc(100vw - 478px)",
    },
    customerDetails: {
      email,
      emailHash: hashedEmail,
      externalId: userId,
      fullName: name,
      chatAvatarUrl: avatarUrl,
    },
    entryPoint: {
      type: "default",
    },
    style: {
      brandColor: "#493FC1",
      brandBackgroundColor: "#FFFFFF",
      launcherBackgroundColor: "#493FC1",
      launcherIconColor: "#FFFFFF",
    },
    links: [
      {
        icon: "book",
        text: "Platform documentation",
        url: DOCS_LINK,
      },
      {
        icon: "error",
        text: "Report critical production incident",
        url: DOCS_REPORT_CRITICAL_INCIDENT,
      },
    ],
    chatButtons: [
      {
        icon: "chat",
        text: "Chat with Taktile Support Bot",
      },
    ],
  };
};

type Args = {
  enabled: boolean;
};

export const useBootWebchat = ({ enabled }: Args) => {
  const {
    signed_in_email,
    signed_in_user_id,
    signed_in_full_name,
    signed_in_username,
    hashed_email,
    signed_in_avatar_url,
  } = useUserData();

  // Boot the Webchat client when the user has signed in
  useEffect(() => {
    // Don't boot the Webchat client in Cypress or if the its not enabled
    if ("Cypress" in window || !enabled) {
      return;
    }

    if (hashed_email) {
      const email =
        signed_in_email ||
        signed_in_username ||
        `user+${signed_in_user_id}@taktile.com`;
      const name =
        signed_in_full_name ||
        signed_in_username?.split("@")?.[0] ||
        `Taktile User ${signed_in_user_id}`;

      setTimeout(() => {
        injectWebchatContainer();
        const config = buildWebchatConfig({
          email,
          name,
          hashedEmail: hashed_email,
          userId: signed_in_user_id ?? "",
          avatarUrl: signed_in_avatar_url ?? "",
        });
        injectWebchatScript(config);
      }, 2000);
    }
  }, [
    signed_in_full_name,
    signed_in_user_id,
    signed_in_email,
    signed_in_username,
    hashed_email,
    signed_in_avatar_url,
    enabled,
  ]);
};

export const toggleWebchatWidget = () => {
  const webchatWindow = window as WebchatWindow;
  if (!webchatWindow.Plain) {
    return;
  }

  if (webchatWindow.isWidgetOpen) {
    webchatWindow.Plain.close();
  } else {
    webchatWindow.Plain.open();
  }
};
